// ** Import from react dom
import { Route, Routes, Navigate } from "react-router-dom";

// ** Import core SCSS styles
import "assets/scss/theme.scss";

// ** Import Layouts
import DashboardIndex from "layouts/dashboard/DashboardIndex";
import AuthLayout from "layouts/dashboard/AuthLayout";
import DefaultLayout from "layouts/marketing/DefaultLayout";
import BlankLayout from "layouts/marketing/BlankLayout";
import NotFound from "layouts/marketing/NotFound";

// ** Import Dashboard Menu Pages
import Overview from "components/dashboard/overview/Overview";
import Analytics from "components/dashboard/analytics/Analytics";

// ** Import Courses Pages
import AllCourses from "components/dashboard/courses/all-courses/AllCourses";
import AllEvents from "components/dashboard/events/AllEvents";
import CoursesCategory from "components/dashboard/courses/CoursesCategory";
import CategorySingle from "components/dashboard/courses/CategorySingle";

// ** Import Users Pages
import Instructor from "components/dashboard/user/Instructor";
import Students from "components/dashboard/user/Students";
import Users from "components/dashboard/user/Users";

// ** Import Authentication components
import SignIn from "components/dashboard/authentication/SignIn";
import SignUp from "components/dashboard/authentication/SignUp";
import ForgetPassword from "components/dashboard/authentication/ForgetPassword";
import Notifications from "components/dashboard/authentication/Notifications";

// ** Import Settings components
import General from "components/dashboard/settings/General";
import Google from "components/dashboard/settings/Google";
import Social from "components/dashboard/settings/Social";
import SocialLogin from "components/dashboard/settings/SocialLogin";
import Payment from "components/dashboard/settings/Payment";
import SMTPServer from "components/dashboard/settings/SMTPServer";

// Dashboard Apps -> Calendar
import Calendar from "components/dashboard/calendar/Calendar";

/* ----------------------------------- */
/* IMPORTS FOR MARKETING PAGES         */
import CourseIndex from "components/marketing/pages/courses/course-index/CourseIndex";
import CourseCategory from "components/marketing/pages/courses/course-category/CourseCategory";
import CourseSingle from "components/marketing/pages/courses/course-single/CourseSingle";
import CourseSingle2 from "components/marketing/pages/courses/course-single2/CourseSingle2";
import CourseResume from "components/marketing/pages/courses/course-resume/CourseResume";
import CourseCheckout from "components/marketing/pages/courses/course-checkout/CourseCheckout";
import CourseFilterPage from "components/marketing/pages/courses/course-filter-page/CourseFilterPage";
import AddNewCourse from "components/marketing/pages/courses/add-new-course/AddNewCourse";
import EditCourse from "components/marketing/pages/courses/add-new-course/EditCourse";
import CourseFeedbackPage from "components/marketing/pages/courses/CourseFeedbackPage";
import CourseMeeting from "components/marketing/pages/courses/CourseMeeting";

/* IMPORTS FOR SPECIALTY PAGES */
import ComingSoon from "components/marketing/pages/specialty/ComingSoon";
import Error404 from "components/marketing/pages/specialty/Error404";
import MaintenanceMode from "components/marketing/pages/specialty/MaintenanceMode";
import TermsAndConditions from "components/marketing/pages/specialty/TermsAndConditions";

// Events Pages
import EventsPage from "components/marketing/pages/events/EventsPage";
import EventDetailPage from "components/marketing/pages/events/EventDetailPage";
import MyCertificates from "components/marketing/pages/events/MyCertificates";
import EventFeedbackPage from "components/marketing/pages/events/EventFeedbackPage";

// Instructor Dashboard Pages
import InstructorDashboard from "components/marketing/instructor/Dashboard";
import InstructorMyCourses from "components/marketing/instructor/MyCourses";
import InstructorReviews from "components/marketing/instructor/Reviews";
import Earnings from "components/marketing/instructor/Earnings";
import InstructorOrders from "components/marketing/instructor/Orders";
import InstructorStudents from "components/marketing/instructor/Students";
import ViewProfile from "components/marketing/instructor/ViewProfile";
import InstructorQuiz from "components/marketing/instructor/Quiz";
import InstructorQuizSingle from "components/marketing/instructor/QuizSingle";
import InstructorQuizResult from "components/marketing/instructor/QuizResult";

// Healthworker Pages
import Patients from "components/marketing/healthworkers/Patients";
import AddPatient from "components/marketing/healthworkers/AddPatient";
import PatientDataForms from "components/marketing/healthworkers/PatientDataForms";
import AddPatientForm from "components/marketing/healthworkers/AddPatientForm";

// Student Dashboard Pages
import StudentDashboard from "components/marketing/student/Dashboard";
import StudentQuiz from "components/marketing/student/Quiz";
import StudentQuizStart from "components/marketing/student/quiz-start/QuizStart";
import StudentQuizAttempt from "components/marketing/student/QuizAttempt";
import StudentQuizResult from "components/marketing/student/QuizResult";
import MobileQuiz from "components/marketing/quiz-mobile/MobileQuiz";

// Account Settings
import DeleteProfile from "components/marketing/account-settings/DeleteProfile";
import EditProfile from "components/marketing/account-settings/EditProfile";
import LinkedAccounts from "components/marketing/account-settings/LinkedAccounts";
import AccountNotifications from "components/marketing/account-settings/Notifications";
import StudentPayment from "components/marketing/account-settings/Payment";
import ProfilePrivacy from "components/marketing/account-settings/ProfilePrivacy";
import Security from "components/marketing/account-settings/Security";
import SocialProfiles from "components/marketing/account-settings/SocialProfiles";
import Subscriptions from "components/marketing/account-settings/Subscriptions";
import BillingInfo from "components/marketing/account-settings/BillingInfo";
import Payouts from "components/marketing/account-settings/Payouts";
import Invoice from "components/marketing/account-settings/Invoice";
import InvoiceDetails from "components/marketing/account-settings/InvoiceDetails";

// Guards & Auth
import AuthGuard from "guards/AuthGuard";
import GuestGuard from "guards/GuestGuard";
import ResetPassword from "components/dashboard/authentication/ResetPassword";

// Dashboard User Management
import AddInstructor from "components/dashboard/user/AddInstructor";
import AddUser from "components/dashboard/user/AddUsers";
import CourseProgress from "components/dashboard/courses/course-progress/CourseProgress";

import { Suspense } from "react";

const AllRoutes = () => {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Routes>
        {/* Routes with DefaultLayout */}
        <Route element={<DefaultLayout />}>
          <Route path="/" element={<CourseIndex />} />
          <Route path="/academy/course-category/" element={<CourseCategory />} />
          <Route
            path="/academy/courses/course-checkout/:guid/"
            element={
              <AuthGuard>
                <CourseCheckout />
              </AuthGuard>
            }
          />
          <Route
            path="/academy/courses/course-filter-page/"
            element={<CourseFilterPage />}
          />
          <Route path="/academy/events/" element={<EventsPage />} />
          <Route path="/academy/events/:guid/" element={<EventDetailPage />} />
          <Route
            path="/academy/courses/feedback/:guid/"
            element={
              <AuthGuard>
                <CourseFeedbackPage />
              </AuthGuard>
            }
          />
          <Route
            path="/academy/events/my-certificates/"
            element={
              <AuthGuard>
                <MyCertificates />
              </AuthGuard>
            }
          />
          <Route
            path="/academy/events/feedback/:guid/"
            element={
              <AuthGuard>
                <EventFeedbackPage />
              </AuthGuard>
            }
          />
          <Route
            path="/academy/instructor/add-new-course/"
            element={<AddNewCourse />}
          />
          <Route
            path="/academy/instructor/edit-course/:guid"
            element={<AddNewCourse />}
          />
          <Route
            path="/academy/instructor/edit-course-details/:guid"
            element={<EditCourse />}
          />
          <Route
            path="/academy/courses/course-single/"
            element={<CourseSingle />}
          />
          <Route
            path="/academy/courses/course-view/:guid"
            element={<CourseSingle2 />}
          />
          <Route
            path="/academy/courses/admin-review/:guid"
            element={<CourseSingle2 />}
          />

          {/* Instructor Dashboard */}
          <Route
            path="/academy/instructor/dashboard/"
            element={
              <AuthGuard>
                <InstructorDashboard />
              </AuthGuard>
            }
          />
          <Route
            path="/academy/instructor/instructor-my-courses/"
            element={
              <AuthGuard>
                <InstructorMyCourses />
              </AuthGuard>
            }
          />
          <Route
            path="/academy/instructor/instructor-reviews/"
            element={
              <AuthGuard>
                <InstructorReviews />
              </AuthGuard>
            }
          />
          <Route
            path="/academy/instructor/instructor-earnings/"
            element={
              <AuthGuard>
                <Earnings />
              </AuthGuard>
            }
          />
          <Route
            path="/academy/instructor/instructor-orders/"
            element={
              <AuthGuard>
                <InstructorOrders />
              </AuthGuard>
            }
          />
          <Route
            path="/academy/instructor/instructor-students/"
            element={
              <AuthGuard>
                <InstructorStudents />
              </AuthGuard>
            }
          />
          <Route
            path="/academy/instructor/instructor-payouts/"
            element={
              <AuthGuard>
                <Payouts />
              </AuthGuard>
            }
          />
          <Route
            path="/academy/instructor/instructor-edit-profile/"
            element={
              <AuthGuard>
                <EditProfile />
              </AuthGuard>
            }
          />
          <Route
            path="/academy/instructor/instructor-profile/"
            element={
              <AuthGuard>
                <ViewProfile />
              </AuthGuard>
            }
          />
          <Route
            path="/academy/instructor/instructor-security/"
            element={
              <AuthGuard>
                <Security />
              </AuthGuard>
            }
          />
          <Route
            path="/academy/instructor/instructor-social-profiles/"
            element={<SocialProfiles />}
          />
          <Route
            path="/academy/instructor/instructor-notifications/"
            element={
              <AuthGuard>
                <AccountNotifications />
              </AuthGuard>
            }
          />
          <Route
            path="/academy/instructor/instructor-profile-privacy/"
            element={
              <AuthGuard>
                <ProfilePrivacy />
              </AuthGuard>
            }
          />
          <Route
            path="/academy/instructor/instructor-delete-profile/"
            element={
              <AuthGuard>
                <DeleteProfile />
              </AuthGuard>
            }
          />
          <Route path="/academy/instructor/quiz/" element={<InstructorQuiz />} />
          <Route
            path="/academy/instructor/quiz/single/"
            element={<InstructorQuizSingle />}
          />
          <Route
            path="/academy/instructor/quiz/result/"
            element={<InstructorQuizResult />}
          />

          {/* Healthworker - nurses, doctors */}
          <Route
            path="/academy/healthworker/patients/"
            element={
              <AuthGuard>
                <Patients />
              </AuthGuard>
            }
          />
          <Route
            path="/academy/healthworker/patients/add"
            element={
              <AuthGuard>
                <AddPatient />
              </AuthGuard>
            }
          />
          <Route
            path="/academy/healthworker/patients/edit/:guid"
            element={
              <AuthGuard>
                <AddPatient />
              </AuthGuard>
            }
          />
          <Route
            path="/academy/healthworker/patients/forms/:guid"
            element={
              <AuthGuard>
                <PatientDataForms />
              </AuthGuard>
            }
          />
          <Route
            path="/academy/healthworker/patients/forms/:guid/add"
            element={
              <AuthGuard>
                <AddPatientForm />
              </AuthGuard>
            }
          />

          {/* Student Dashboard */}
          <Route
            path="/academy/student/dashboard/"
            element={
              <AuthGuard>
                <StudentDashboard />
              </AuthGuard>
            }
          />
          <Route
            path="/academy/student/student-subscriptions/"
            element={
              <AuthGuard>
                <Subscriptions />
              </AuthGuard>
            }
          />
          <Route
            path="/academy/student/student-billing-info/"
            element={
              <AuthGuard>
                <BillingInfo />
              </AuthGuard>
            }
          />
          <Route
            path="/academy/student/student-payment/"
            element={
              <AuthGuard>
                <StudentPayment />
              </AuthGuard>
            }
          />
          <Route
            path="/academy/student/student-invoice/"
            element={
              <AuthGuard>
                <Invoice />
              </AuthGuard>
            }
          />
          <Route
            path="/academy/student/student-invoice-details/"
            element={
              <AuthGuard>
                <InvoiceDetails />
              </AuthGuard>
            }
          />
          <Route
            path="/academy/student/student-edit-profile/"
            element={
              <AuthGuard>
                <EditProfile />
              </AuthGuard>
            }
          />
          <Route
            path="/academy/student/student-security/"
            element={
              <AuthGuard>
                <Security />
              </AuthGuard>
            }
          />
          <Route
            path="/academy/student/student-social-profiles/"
            element={<SocialProfiles />}
          />
          <Route
            path="/academy/student/student-notifications/"
            element={
              <AuthGuard>
                <AccountNotifications />
              </AuthGuard>
            }
          />
          <Route
            path="/academy/student/student-profile-privacy/"
            element={<ProfilePrivacy />}
          />
          <Route
            path="/academy/student/student-delete-profile/"
            element={
              <AuthGuard>
                <DeleteProfile />
              </AuthGuard>
            }
          />
          <Route
            path="/academy/student/student-linked-accounts/"
            element={<LinkedAccounts />}
          />
          <Route path="/academy/student/quiz/" element={<StudentQuiz />} />
          <Route
            path="/academy/student/quiz/attempt/"
            element={<StudentQuizAttempt />}
          />
          <Route
            path="/academy/student/quiz/start/"
            element={<StudentQuizStart />}
          />
          <Route
            path="/academy/student/quiz/result/"
            element={<StudentQuizResult />}
          />
        </Route>

        {/* Routes with BlankLayout */}
        <Route element={<BlankLayout />}>
          <Route
            path="/academy/quiz/mobile/:course/:guid"
            element={<MobileQuiz />}
          />
          <Route
            path="/academy/courses/course-meeting/:guid/"
            element={<CourseMeeting />}
          />
          <Route
            path="/academy/courses/course-resume/:guid"
            element={<CourseResume />}
          />
          <Route
            path="/academy/course/review/:guid"
            element={<CourseResume />}
          />
          <Route
            path="/academy/specialty/terms-and-conditions/"
            element={<TermsAndConditions />}
          />
        </Route>

        {/* Routes with NotFound */}
        <Route element={<NotFound />}>
          <Route
            path="/academy/specialty/coming-soon/"
            element={<ComingSoon />}
          />
          <Route path="/academy/specialty/404-error/" element={<Error404 />} />
          <Route
            path="/academy/specialty/maintenance-mode/"
            element={<MaintenanceMode />}
          />
        </Route>

        {/* Routes with AuthLayout */}
        <Route element={<AuthLayout />}>
          <Route
            path="/authentication/sign-in"
            element={
              <GuestGuard>
                <SignIn />
              </GuestGuard>
            }
          />
          <Route path="/authentication/sign-up" element={<SignUp />} />
          <Route
            path="/authentication/forget-password"
            element={<ForgetPassword />}
          />
          <Route
            path="/authentication/reset-password"
            element={<ResetPassword />}
          />
        </Route>

        {/* Routes (DASHBOARD) with DashboardIndex */}
        <Route
          element={
            <AuthGuard>
              <DashboardIndex />
            </AuthGuard>
          }
        >
          <Route path="/dashboard/overview" element={<Overview />} />
          <Route path="/dashboard/analytics" element={<Analytics />} />
          <Route path="/courses/all-courses" element={<AllCourses />} />
          <Route path="/events/all-events" element={<AllEvents />} />
          <Route path="/courses/courses-category" element={<CoursesCategory />} />
          <Route path="/courses/category-single" element={<CategorySingle />} />
          <Route path="/user/instructor" element={<Instructor />} />
          <Route path="/user/instructor/add-new" element={<AddInstructor />} />
          <Route path="/user/user/add-new" element={<AddUser />} />
          <Route path="/user/user/edit/:guid" element={<AddUser />} />
          <Route path="/user/users" element={<Users />} />
          <Route path="/user/students" element={<Students />} />
          <Route path="/courses/progress/:guid" element={<CourseProgress />} />
          <Route
            path="/authentication/notifications"
            element={<Notifications />}
          />

          {/* SETTINGS */}
          <Route path="/settings/general" element={<General />} />
          <Route path="/settings/google" element={<Google />} />
          <Route path="/settings/social" element={<Social />} />
          <Route path="/settings/social-login" element={<SocialLogin />} />
          <Route path="/settings/payment" element={<Payment />} />
          <Route path="/settings/smtp-server" element={<SMTPServer />} />

          <Route path="/dashboard/calendar" element={<Calendar />} />
        </Route>

        {/* Redirect unknown routes to 404 */}
        <Route
          path="*"
          element={<Navigate to="/academy/specialty/404-error/" replace />}
        />
      </Routes>
    </Suspense>
  );
};

export default AllRoutes;
